import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, ConfirmModal, FriendlyError } from "@codel/dova-ds";
import { cancelSessionMeSessionsSessionUuidCancelPost } from "@/__generated__/api/me/me";
import { useInvalidateSessions, type MeSession } from "../hooks/sessionHooks";
import { SESSION_CHANGE_COPY } from "./rescheduleCopy";
import { COUPLE_EVENTS } from "../analytics/events";
import { track } from "../analytics/track";

export function SessionChangeActions({ session }: { session: MeSession }) {
  const navigate = useNavigate();
  const invalidateSessions = useInvalidateSessions();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [cancelError, setCancelError] = useState<Error | null>(null);

  const cancelSession = async () => {
    setIsCanceling(true);
    setCancelError(null);
    // Emitted before the POST, not after it succeeds: the client-without-server
    // delta this event exists for (§ PR description) is cancels that were
    // submitted through this UI but never completed server-side — gating on
    // POST success would erase exactly that population.
    track(COUPLE_EVENTS.sessionCanceled, {
      session_id: session.uuid,
      is_standing: session.fromStandingSlot,
    });
    try {
      await cancelSessionMeSessionsSessionUuidCancelPost(session.uuid, {
        expected_start_utc: session.startUtc,
      });
      invalidateSessions();
      navigate("/me/home");
    } catch (error) {
      setCancelError(error instanceof Error ? error : new Error(SESSION_CHANGE_COPY.cancelFailed));
    } finally {
      setIsCanceling(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {/* One row, two equal outline buttons: the session's two changes side by side rather
          than a stack. Cancel is deliberately NOT styled destructive here: the label says what
          it does, the confirm dialog carries the danger styling, and a red button on the hub
          would pull the eye to the one action nobody should be nudged towards. */}
      <div className="grid grid-cols-2 gap-2">
        <Button
          full
          size="xl"
          variant="outline"
          disabled={isCanceling}
          onClick={() => navigate(`/me/sessions/${session.uuid}/reschedule`)}
        >
          {SESSION_CHANGE_COPY.rescheduleLabel}
        </Button>
        <Button full size="xl" variant="outline" disabled={isCanceling} onClick={() => setCancelOpen(true)}>
          {SESSION_CHANGE_COPY.cancelLabel}
        </Button>
      </div>
      <FriendlyError detail={cancelError?.message ?? null} />
      <ConfirmModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title={SESSION_CHANGE_COPY.cancelTitle}
        body={SESSION_CHANGE_COPY.cancelBody}
        confirmLabel={SESSION_CHANGE_COPY.cancelLabel}
        onConfirm={() => void cancelSession()}
        destructive
        dismissOnScrim={!isCanceling}
      />
    </div>
  );
}
